import { PopupCampaignStatus } from "../api/generated/models/PopupCampaignStatus";

export const POPUP_MIN_CREATIVE_WIDTH = 1080;
export const POPUP_MIN_CREATIVE_HEIGHT = 1350;
export const POPUP_CREATIVE_ASPECT = 4 / 5;
export const POPUP_NAME_MAX = 200;
export const POPUP_BODY_MAX = 400;
export const POPUP_CTA_TEXT_MAX = 40;
export const POPUP_CTA_URL_MAX = 2000;

const pad = (n: number) => n.toString().padStart(2, "0");

/** ISO instant (or anything Date can parse) -> `YYYY-MM-DDTHH:mm` in local time; "" when empty/invalid. */
export const toDatetimeLocal = (value: unknown): string => {
  if (!value) return "";
  const d = new Date(value as string);
  if (isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** `datetime-local` value -> ISO string; undefined when empty so the backend receives null. */
export const datetimeLocalToIso = (value: string): string | undefined => {
  if (!value) return undefined;
  const d = new Date(value);
  return isNaN(d.getTime()) ? undefined : d.toISOString();
};

/** Mirrors the backend rule: absolute http(s) URL, or an app path starting with "/". */
export const isValidCtaUrl = (url: string): boolean => {
  if (!url || url !== url.trim()) return false;
  if (url.startsWith("/")) return true;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

export interface PopupCampaignFormValues {
  name: string;
  imageAssetId: string;
  body: string;
  ctaText: string;
  ctaUrl: string;
  startAt: string;
  endAt: string;
}

export type PopupCampaignFormErrors = Partial<Record<keyof PopupCampaignFormValues, string>>;

export const validatePopupCampaignForm = (v: PopupCampaignFormValues): PopupCampaignFormErrors => {
  const errors: PopupCampaignFormErrors = {};

  if (!v.name.trim()) errors.name = "Name is required";
  else if (v.name.length > POPUP_NAME_MAX) errors.name = `Name must be at most ${POPUP_NAME_MAX} characters`;

  if (!v.imageAssetId) errors.imageAssetId = "Creative is required";

  if (!v.body.trim()) errors.body = "Body text is required";
  else if (v.body.length > POPUP_BODY_MAX) errors.body = `Body must be at most ${POPUP_BODY_MAX} characters`;

  if (!v.ctaText.trim()) errors.ctaText = "CTA text is required";
  else if (v.ctaText.length > POPUP_CTA_TEXT_MAX) errors.ctaText = `CTA text must be at most ${POPUP_CTA_TEXT_MAX} characters`;

  if (!v.ctaUrl.trim()) errors.ctaUrl = "CTA destination is required";
  else if (v.ctaUrl.length > POPUP_CTA_URL_MAX) errors.ctaUrl = `CTA destination must be at most ${POPUP_CTA_URL_MAX} characters`;
  else if (!isValidCtaUrl(v.ctaUrl)) errors.ctaUrl = "Must be an absolute http(s) URL or a path starting with /";

  if (v.startAt && v.endAt) {
    const start = new Date(v.startAt).getTime();
    const end = new Date(v.endAt).getTime();
    if (!(end > start)) errors.endAt = "End must be after start";
  }

  return errors;
};

/** Backend fractions are 0..1. */
export const formatPercent = (fraction: number | null | undefined): string =>
  `${((fraction ?? 0) * 100).toFixed(2)}%`;

export const statusLabel = (status: PopupCampaignStatus | undefined): string => {
  switch (status) {
    case PopupCampaignStatus.ACTIVE:
      return "Active";
    case PopupCampaignStatus.INACTIVE:
      return "Inactive";
    default:
      return "Draft";
  }
};

export const statusBadgeClass = (status: PopupCampaignStatus | undefined): string => {
  switch (status) {
    case PopupCampaignStatus.ACTIVE:
      return "bg-green-100 text-green-700";
    case PopupCampaignStatus.INACTIVE:
      return "bg-red-100 text-red-700";
    default:
      return "bg-gray-100 text-gray-500";
  }
};

/** react-easy-crop rounds the pixel crop, so allow a 2 px tolerance. */
export const isCropLargeEnough = (
  width: number,
  height: number,
  minWidth: number,
  minHeight: number
): boolean => width >= minWidth - 2 && height >= minHeight - 2;

/** Best-effort message from a generated-client ApiError or axios error; falls back to the given text. */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  const e = error as any;
  const message = e?.body?.message ?? e?.response?.data?.message;
  return typeof message === "string" && message.trim() ? message : fallback;
};

/** RFC 4122-ish GUID check (case-insensitive), used to gate destructive by-id actions. */
export const isGuid = (value: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());

export const DEFAULT_BRANCH_LINK_ORIGIN = "https://oisterbonus.app.link";

/**
 * Branch.io long link (all data inline, no API call) that opens the pop-up preview:
 * in the native app via `$canonical_url`, in a browser via `$fallback_url`.
 *
 * `$canonical_url` is REQUIRED and must not be removed to shrink the QR code. The native app's
 * Branch session handler (rewardchain-flutter `application.dart`, `FlutterBranchSdk.listSession`)
 * reads `$canonical_url` and nothing else — it ignores `popupPreview` and `$fallback_url` — so a
 * link without it opens the app to a blank home screen and the preview never appears. Dropping it
 * on the grounds that "Branch only uses it for content indexing" is exactly the bug this comment
 * exists to prevent; it is true of Branch in general and false for this app.
 *
 * The link is rendered as a QR code, so length still matters: at three copies of the 48-character
 * token it is ~328 characters (QR version 12), which the PreviewModal renders at 288px to keep the
 * module size scannable. Keep the token compact — see the backend's PopupPreviewTokenService, whose
 * ~290-character JWT predecessor pushed this QR past the scannable limit.
 */
export const buildPopupPreviewBranchLink = (
  previewUrl: string,
  token: string,
  origin: string = process.env.REACT_APP_BRANCH_LINK_ORIGIN || DEFAULT_BRANCH_LINK_ORIGIN
): string => {
  const params = new URLSearchParams();
  params.set("popupPreview", token);
  params.set("$canonical_url", previewUrl);
  params.set("$fallback_url", previewUrl);
  return `${origin.replace(/\/+$/, "")}/?${params.toString()}`;
};
